import { Key, useContext, useState } from 'react'
import { Folder, FolderSearchResult, List, ListState } from '../../types'
import { SearchTableQueryState, Table } from '../../components'
import Tag, { TagVariant } from '../../ui/Tag'
import { snakeToTitle } from '../../utils'
import { useRoute } from '../router'
import Menu, { MenuItem } from '../../ui/Menu'
import { ArchiveIcon, DuplicateIcon, EditIcon, FolderMoveIcon } from '../../ui/icons'
import api from '../../api'
import { useNavigate, useParams } from 'react-router'
import { Translation, useTranslation } from 'react-i18next'
import { ProjectContext } from '../../contexts'
import toast from 'react-hot-toast/headless'
import { FolderSelectModal } from '../../components/table/FolderSelectModal'
import { TagPicker } from '../settings/TagPicker'
import { MultiSelect } from '../../ui/form/MultiSelect'
import { listTypes } from './ListFields'

interface ListTableParams {
    state: SearchTableQueryState<FolderSearchResult<List>>
    title?: string
    selectedRow?: Key
    onSelectRow?: (list: List) => void
}

export const ListTag = ({ state, progress }: Pick<List, 'state' | 'progress'>) => {
    const variant: Record<ListState, TagVariant> = {
        draft: 'plain',
        loading: 'info',
        ready: 'success',
    }

    const complete = progress?.complete ?? 0
    const total = progress?.total ?? 0
    const percent = total > 0 ? complete / total : 0
    const percentStr = percent.toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 0 })

    return <Tag variant={variant[state]}>
        <Translation>{ (t) => t(state) }</Translation>
        {progress && ` (${percentStr})`}
    </Tag>
}

export default function ListTable({ state, selectedRow, onSelectRow, title }: ListTableParams) {
    const route = useRoute()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const { projectId = '' } = useParams()
    const [listToMove, setListToMove] = useState<List | undefined>()

    function handleOnSelectRow(list: List) {
        onSelectRow ? onSelectRow(list) : route(`lists/${list.id}`)
    }

    const handleDuplicateList = async (id: number) => {
        const list = await api.lists.duplicate(projectId, id)
        await navigate(list.id.toString())
    }

    const handleMoveFolder = async (folder: Folder) => {
        if (!listToMove) return
        const { name, description, rule, tags } = listToMove
        await api.lists.update(project.id, listToMove.id, { name, description, rule, tags, folder_id: folder.id })
        setListToMove(undefined)
        await state.reload()
        toast.success(t('folder_moved_item', { item: listToMove.name, folder: folder.name }))
    }

    const handleArchiveList = async (id: number) => {
        await api.lists.delete(projectId, id)
        await state.reload()
    }

    return <>
        <Table
            {...state}
            type="folder"
            title={title}
            itemKey={({ item }) => item.id}
            columns={[
                {
                    key: 'name',
                    title: t('name'),
                    sortable: true,
                    minWidth: '200px',
                    cell: ({ item: { name, description } }) => (
                        <div className="multi-cell no-image">
                            <div className="text">
                                <div className="title">{name}</div>
                                {description && <div className="subtitle">
                                    {description}</div>}
                            </div>
                        </div>
                    ),
                },
                {
                    key: 'type',
                    title: t('type'),
                    cell: ({ item }) => <>
                        {snakeToTitle(item.type)}
                        {item.source === 'mixpanel' && <> · <Tag variant="info">Mixpanel</Tag></>}
                    </>,
                    sortable: true,
                },
                {
                    key: 'state',
                    title: t('state'),
                    cell: ({ item: { state } }) => ListTag({ state }),
                    sortable: true,
                },
                {
                    key: 'users_count',
                    title: t('users_count'),
                    cell: ({ item }) => item.users_count?.toLocaleString(),
                },
                {
                    key: 'created_at',
                    title: t('created_at'),
                    sortable: true,
                },
                {
                    key: 'updated_at',
                    title: t('updated_at'),
                    sortable: true,
                },
                {
                    key: 'options',
                    title: t('options'),
                    align: 'end',
                    cell: ({ item }) => (
                        <Menu size="small">
                            <MenuItem icon={<EditIcon />} onClick={() => handleOnSelectRow(item)}>
                                {t('edit')}
                            </MenuItem>
                            <MenuItem icon={<FolderMoveIcon />} onClick={async () => setListToMove(item)}>
                                {t('folder_move')}
                            </MenuItem>
                            <MenuItem icon={<DuplicateIcon />} onClick={async () => await handleDuplicateList(item.id)}>
                                {t('duplicate')}
                            </MenuItem>
                            <MenuItem icon={<ArchiveIcon />} onClick={async () => await handleArchiveList(item.id)}>
                                {t('archive')}
                            </MenuItem>
                        </Menu>
                    ),
                },
            ]}
            selectedRow={selectedRow}
            onSelectRow={list => handleOnSelectRow(list)}
            enableSearch
            filters={[
                <MultiSelect
                    key="type"
                    size="small"
                    options={listTypes(t)}
                    value={state.params.filter?.type}
                    onChange={value => state.setParams({
                        ...state.params,
                        filter: {
                            ...state.params.filter,
                            type: value.length ? value : undefined,
                        },
                    })}
                    placeholder={t('type')}
                    toValue={v => v.key}
                />,
                <TagPicker
                    key="tags"
                    entity="lists"
                    size="small"
                    value={state.params.filter?.tags ?? []}
                    onChange={value => state.setParams({
                        ...state.params,
                        filter: {
                            ...state.params.filter,
                            tags: value.length ? value : undefined,
                        },
                    })}
                    placeholder={t('tags')}
                />,
            ]}
        />

        {!!listToMove && <FolderSelectModal
            type="list"
            open={!!listToMove}
            onClose={() => setListToMove(undefined)}
            onSelect={handleMoveFolder}
        />}
    </>
}
